import React, { useEffect, useMemo, useState } from 'react';
import { getApiBase } from '../../lib/apiBase';
import { redirectIfDashboardIpDenied } from '../../lib/dashboardNetworkAccess';
import { getDashboardAuthHeaders } from '../../lib/dashboardSession';
import MetricSmallBox from './MetricSmallBox';
import { formatDate } from './formatters';

function formatRating(n) {
  const r = Number(n) || 0;
  if (!r) return '';
  return '★'.repeat(Math.max(0, Math.min(5, r))) + '☆'.repeat(Math.max(0, 5 - r));
}

function formatAvgRating(avg) {
  if (!Number.isFinite(avg) || avg <= 0) return '—';
  return `${avg.toFixed(1)} / 5`;
}

function rowMatchesSearch(row, q) {
  if (!q) return true;
  const hay = [
    row.name,
    row.comments,
    row.sessionId,
    formatDate(row.createdAt),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

export default function FeedbackView({ onError, refreshToken }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [ratingFilter, setRatingFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${getApiBase()}/api/dashboard/feedback?limit=500`, {
          headers: getDashboardAuthHeaders(),
        });
        const data = await res.json().catch(() => ({}));
        if (redirectIfDashboardIpDenied(res, data)) return;
        if (!res.ok) {
          onError?.(data.error || `Error ${res.status}`);
          if (!cancelled) setRows([]);
          return;
        }
        if (!cancelled) setRows(Array.isArray(data.feedback) ? data.feedback : []);
      } catch (e) {
        onError?.(e?.message || 'Failed to load feedback');
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onError, refreshToken]);

  const summary = useMemo(() => {
    const total = rows.length;
    const ratings = rows.map((r) => Number(r.rating)).filter((n) => n >= 1 && n <= 5);
    const avg = ratings.length
      ? ratings.reduce((a, b) => a + b, 0) / ratings.length
      : 0;
    return { total, avg };
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (ratingFilter !== 'all' && Number(r.rating) !== Number(ratingFilter)) return false;
      return rowMatchesSearch(r, q);
    });
  }, [rows, search, ratingFilter]);

  const hasFilters = search.trim() !== '' || ratingFilter !== 'all';

  return (
    <div className="container-fluid">
      <div className="row">
        <MetricSmallBox
          icon="fas fa-comment-dots"
          label="Total feedback received"
          value={loading ? '…' : summary.total}
          index={0}
        />
        <MetricSmallBox
          icon="fas fa-star"
          label="Average rating"
          value={loading ? '…' : formatAvgRating(summary.avg)}
          sub={summary.total > 0 ? formatRating(Math.round(summary.avg)) : null}
          index={1}
        />
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between">
              <h3 className="card-title mb-0">User feedback</h3>
              <div className="d-flex flex-wrap align-items-center mt-2 mt-md-0">
                <input
                  type="search"
                  className="form-control form-control-sm mr-2 mb-2"
                  style={{ minWidth: 200 }}
                  placeholder="Search name, comments, session…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label="Search feedback"
                />
                <select
                  className="form-control form-control-sm mr-2 mb-2"
                  style={{ width: 140 }}
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                  aria-label="Filter by rating"
                >
                  <option value="all">All ratings</option>
                  <option value="5">5 stars</option>
                  <option value="4">4 stars</option>
                  <option value="3">3 stars</option>
                  <option value="2">2 stars</option>
                  <option value="1">1 star</option>
                </select>
                {hasFilters && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary mb-2"
                    onClick={() => {
                      setSearch('');
                      setRatingFilter('all');
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="p-3 text-muted">
                  <i className="fas fa-spinner fa-spin mr-1" />
                  Loading…
                </div>
              ) : rows.length === 0 ? (
                <div className="p-3 text-muted">No feedback yet.</div>
              ) : filteredRows.length === 0 ? (
                <div className="p-3 text-muted">No feedback matches your filters.</div>
              ) : (
                <>
                  <div className="px-3 py-2 border-bottom text-muted small">
                    Showing {filteredRows.length} of {rows.length} entries
                  </div>
                  <div className="table-responsive">
                    <table className="table table-striped mb-0">
                      <thead>
                        <tr>
                          <th>When</th>
                          <th>Name</th>
                          <th>Rating</th>
                          <th>Comments</th>
                          <th>Session</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.map((r) => (
                          <tr key={r.id}>
                            <td className="text-sm">{formatDate(r.createdAt)}</td>
                            <td className="text-sm">
                              {r.name || <span className="text-muted">(anon)</span>}
                            </td>
                            <td className="text-warning font-weight-bold">{formatRating(r.rating)}</td>
                            <td style={{ maxWidth: 520, whiteSpace: 'pre-wrap' }}>{r.comments || ''}</td>
                            <td className="text-monospace text-sm">
                              {r.sessionId ? String(r.sessionId).slice(0, 8) : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
